import React, { useEffect, useState } from 'react'
import { getAuth } from 'firebase/auth'
import { doc, getDoc } from 'firebase/firestore'
import { db } from '../lib/firebase'

/**
 * AdminSessionGuard - Prevents admin session from interfering with customer site
 * 
 * When admin clicks "Xem website" from admin panel, their Firebase auth session
 * carries over to the customer site. This guard detects admin sessions and shows
 * a warning instead of trying to load customer data (which would cause performance
 * issues or crashes).
 */
export const AdminSessionGuard = ({ children }) => {
    const [isAdminSession, setIsAdminSession] = useState(false)
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        const checkSession = async () => {
            const auth = getAuth()
            const user = auth.currentUser

            if (user) {
                try {
                    const adminDoc = await getDoc(doc(db, 'admins', user.uid))
                    if (adminDoc.exists()) {
                        setIsAdminSession(true)
                    }
                } catch (error) {
                    console.error('Error checking admin session:', error)
                }
            }

            setLoading(false)
        }

        checkSession()
    }, [])

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        )
    }

    if (isAdminSession) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
                <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
                    <div className="text-center">
                        <div className="text-6xl mb-4">⚠️</div>
                        <h1 className="text-2xl font-bold text-gray-900 mb-4">
                            Phiên Admin Đang Hoạt Động
                        </h1>
                        <p className="text-gray-600 mb-6">
                            Bạn đang đăng nhập với tài khoản quản trị viên.
                            Để sử dụng website với tư cách khách hàng, vui lòng:
                        </p>
                        <div className="space-y-3 text-left mb-6">
                            <div className="flex items-start gap-3">
                                <span className="text-blue-600 font-bold">1.</span>
                                <span className="text-gray-700">Đăng xuất khỏi tài khoản admin</span>
                            </div>
                            <div className="flex items-start gap-3">
                                <span className="text-blue-600 font-bold">2.</span>
                                <span className="text-gray-700">Mở trình duyệt ẩn danh (Incognito/Private)</span>
                            </div>
                            <div className="flex items-start gap-3">
                                <span className="text-blue-600 font-bold">3.</span>
                                <span className="text-gray-700">Hoặc sử dụng trình duyệt khác</span>
                            </div>
                        </div>
                        <div className="flex gap-3">
                            <a
                                href="/admin"
                                className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
                            >
                                Quay lại Admin
                            </a>
                            <button
                                onClick={() => {
                                    getAuth().signOut().then(() => {
                                        window.location.reload()
                                    })
                                }}
                                className="flex-1 px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition-colors"
                            >
                                Đăng xuất
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    return children
}

export default AdminSessionGuard

